import { useStore } from '@nanostores/react';
import { memo, useState, type ReactNode } from 'react';
import { Button } from '~/components/ui/Button';
import { Table, TableHead, TableRow } from '~/components/ui/Table';
import * as Tooltip from '~/components/ui/Tooltip';
import { UpgradePlanDialogs } from '~/components/ui/pricing/UpgradePlanDialogs';
import { userStore } from '~/lib/stores/user';
import { classNames } from '~/utils/classNames';

const GENERAL_TABLE_TITLE_CLASSES = 'justify-self-center text-bolt-ds-textPrimary font-semibold text-sm';

const TABLE_ROWS_DATA = [
  {
    name: 'Bandwidth',
    free: 'Up to 10 GB',
    pro: 'Up to 30 GB',
  },
  {
    name: 'Web requests',
    free: 'Up to 333,333',
    pro: 'Up to 1,000,000',
  },
  {
    name: 'Custom domain',
    free: '❌ No',
    pro: '✅ Yes',
  },
  {
    name: 'Pay as you go',
    tooltip: 'Use as much hosting capacity as you need, under a budget you can set.',
    free: '❌ No',
    pro: '✅ Yes',
  },
];

interface CapacityTableProps {
  isFreeUser: boolean;
}

export const CapacityTable = memo(({ isFreeUser }: CapacityTableProps) => {
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const activeOrganization = useStore(userStore.getActiveOrganization());

  return (
    <Table className="w-full min-h-0 overflow-y-auto">
      <TableHead
        className={classNames('grid top-0 z-1 sticky', isFreeUser ? 'grid-cols-[3fr_1fr_1fr]' : 'grid-cols-[3fr_1fr]')}
      >
        <span>Hosting metric</span>
        {isFreeUser && <span className={GENERAL_TABLE_TITLE_CLASSES}>Free</span>}
        <span className={GENERAL_TABLE_TITLE_CLASSES}>Paid</span>
      </TableHead>

      {TABLE_ROWS_DATA.map((item, index) => (
        <TableRow
          key={`table-row-${index}`}
          className={classNames(
            'grid bg-bolt-ds-surface items-center py-0 text-sm',
            isFreeUser ? 'grid-cols-[3fr_1fr_1fr]' : 'grid-cols-[3fr_1fr]',
          )}
        >
          <span className="font-medium lh-[20px] py-4 flex flex-row gap-2 items-center">
            {item.name}
            {item.tooltip && (
              <SimpleTooltip content={item.tooltip}>
                <span className="text-bolt-ds-textSecondary i-heroicons:information-circle" />
              </SimpleTooltip>
            )}
          </span>
          {isFreeUser && <span className="justify-self-center">{item.free}</span>}
          <span className="justify-self-center">{item.pro}</span>
        </TableRow>
      ))}
      {isFreeUser && (
        <TableRow className="grid bg-bolt-ds-surface items-center py-0 text-sm grid-cols-[3fr_1fr_1fr]">
          <span className="font-medium lh-[20px] py-4">Subscription</span>
          <span className="justify-self-center text-xs px-2 py-1.5 rounded-md bg-bolt-ds-inverseSurface/10 opacity-50">
            Current plan
          </span>
          <span className="justify-self-center">
            <Button
              size="sm"
              onClick={() => {
                setIsUpgradeModalOpen(true);
              }}
            >
              Upgrade
            </Button>
          </span>
        </TableRow>
      )}
      <UpgradePlanDialogs
        isOpen={isUpgradeModalOpen}
        cards={[!activeOrganization ? 'pro' : 'teams']}
        onClose={() => setIsUpgradeModalOpen(false)}
        message="Attaching custom domains to your site is a Pro feature, upgrade to Pro to unlock it."
      />
    </Table>
  );
});

const SimpleTooltip = ({ children, content }: { children: ReactNode; content: string }) => {
  return (
    <Tooltip.Provider>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <div className="flex">{children}</div>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content side="right" align="center" maxWidth={260} truncate={false}>
            {content}
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};
